// src/contexts/CitationTooltipContext.tsx

import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import type { CitationTooltipGroup } from "./citationTooltipGroup";
import { wrapIndex } from "./citationTooltipGroup";

/** What the tooltip shows for one web citation. */
export interface WebCitationTooltipData {
  source: string;
  url: string | undefined;
  /** Actual article title from web search results (if resolved) */
  title: string | undefined;
}

/**
 * State for the global citation tooltip.
 * Managed at a high level to prevent flickering during streaming.
 */
interface CitationTooltipState {
  isVisible: boolean;
  anchorRect: DOMRect | null;
  source: string;
  url: string | undefined;
  /** Actual article title from web search results (if resolved) */
  title: string | undefined;
  /**
   * A citation spliced in from Gemini's grounding, shown with the site-first
   * tooltip. False for Claude's own citations, which keep the original tooltip.
   */
  grounded: boolean;
  /** Set when a merged chip is shown; null for a single citation. */
  group: CitationTooltipGroup<WebCitationTooltipData> | null;
  /** Opened by click/Enter on a merged chip: ignores mouse leave until closed. */
  pinned: boolean;
}

/**
 * Context interface for citation tooltip management
 */
interface CitationTooltipContextType {
  state: CitationTooltipState;
  showTooltip: (data: {
    anchorRect: DOMRect | null;
    source: string;
    url: string | undefined;
    title?: string;
    grounded?: boolean;
  }) => void;
  /**
   * Show a merged chip's citations, paged. Re-showing the same chip keeps its
   * page. Web citations are deliberately not tracked in analytics.
   */
  showTooltipGroup: (
    anchorEl: HTMLElement,
    items: WebCitationTooltipData[],
    pinned: boolean
  ) => void;
  /** Move the merged chip's tooltip by `delta` citations, wrapping around. */
  pageTooltip: (delta: number) => void;
  /** Hide now, pinned or not. */
  closeTooltip: () => void;
  hideTooltip: () => void;
  /** Keep tooltip visible when mouse enters tooltip area */
  handleTooltipMouseEnter: () => void;
  /** Hide tooltip when mouse leaves tooltip area */
  handleTooltipMouseLeave: () => void;
}

const defaultState: CitationTooltipState = {
  isVisible: false,
  anchorRect: null,
  source: "",
  url: undefined,
  title: undefined,
  grounded: false,
  group: null,
  pinned: false,
};

const CitationTooltipContext = createContext<CitationTooltipContextType | undefined>(undefined);

interface CitationTooltipProviderProps {
  children: React.ReactNode;
  /**
   * Optional ref to the scroll container. When provided, the tooltip will
   * automatically hide when the container is scrolled.
   */
  scrollContainerRef?: React.RefObject<HTMLElement>;
}

/**
 * Provider for citation tooltip state.
 * Place at a high level (e.g., ChatMessages) so tooltip never unmounts during streaming.
 */
export const CitationTooltipProvider: React.FC<CitationTooltipProviderProps> = ({
  children,
  scrollContainerRef,
}) => {
  const [state, setState] = useState<CitationTooltipState>(defaultState);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref mirror of isVisible for O(1) checks in high-frequency scroll handler
  const isVisibleRef = useRef(false);
  const pinnedRef = useRef(false);
  const groupRef = useRef<CitationTooltipGroup<WebCitationTooltipData> | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const showTooltip = useCallback(
    (data: {
      anchorRect: DOMRect | null;
      source: string;
      url: string | undefined;
      title?: string;
      grounded?: boolean;
    }) => {
      clearHideTimeout();
      isVisibleRef.current = true;
      pinnedRef.current = false;
      groupRef.current = null;
      setState({
        isVisible: true,
        anchorRect: data.anchorRect,
        source: data.source,
        url: data.url,
        title: data.title,
        grounded: data.grounded ?? false,
        group: null,
        pinned: false,
      });
    },
    [clearHideTimeout]
  );

  const showTooltipGroup = useCallback(
    (anchorEl: HTMLElement, items: WebCitationTooltipData[], pinned: boolean) => {
      if (items.length === 0) return;
      clearHideTimeout();
      const current = groupRef.current;
      const sameChip = isVisibleRef.current && current?.anchorEl === anchorEl;
      const group = {
        items,
        index: sameChip && current ? Math.min(current.index, items.length - 1) : 0,
        anchorEl,
      };
      const nextPinned = pinned || (sameChip && pinnedRef.current);

      isVisibleRef.current = true;
      pinnedRef.current = nextPinned;
      groupRef.current = group;
      setState({
        isVisible: true,
        anchorRect: anchorEl.getBoundingClientRect(),
        ...items[group.index],
        // Only Gemini's citations merge, so a group is always grounded.
        grounded: true,
        group,
        pinned: nextPinned,
      });
    },
    [clearHideTimeout]
  );

  const pageTooltip = useCallback((delta: number) => {
    const current = groupRef.current;

    if (!current || !isVisibleRef.current) return;
    const group = { ...current, index: wrapIndex(current.index + delta, current.items.length) };

    groupRef.current = group;
    setState((prev) => ({ ...prev, ...group.items[group.index], group }));
  }, []);

  const closeTooltip = useCallback(() => {
    clearHideTimeout();
    isVisibleRef.current = false;
    pinnedRef.current = false;
    setState((prev) => ({ ...prev, isVisible: false, pinned: false }));
  }, [clearHideTimeout]);

  const hideTooltip = useCallback(() => {
    if (pinnedRef.current) return;
    // Small delay before hiding to allow mouse to move to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      isVisibleRef.current = false;
      setState((prev) => ({ ...prev, isVisible: false }));
    }, 100);
  }, []);

  const handleTooltipMouseEnter = useCallback(() => {
    clearHideTimeout();
  }, [clearHideTimeout]);

  const handleTooltipMouseLeave = useCallback(() => {
    if (pinnedRef.current) return;
    isVisibleRef.current = false;
    setState((prev) => ({ ...prev, isVisible: false }));
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Hide tooltip on scroll (browser doesn't fire onMouseLeave when content scrolls away)
  React.useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!isVisibleRef.current) return; // Skip work when tooltip already hidden

      clearHideTimeout();
      isVisibleRef.current = false;
      pinnedRef.current = false;
      setState((prev) => ({ ...prev, isVisible: false, pinned: false }));
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [scrollContainerRef, clearHideTimeout]);

  return (
    <CitationTooltipContext.Provider
      value={{
        state,
        showTooltip,
        showTooltipGroup,
        pageTooltip,
        closeTooltip,
        hideTooltip,
        handleTooltipMouseEnter,
        handleTooltipMouseLeave,
      }}
    >
      {children}
    </CitationTooltipContext.Provider>
  );
};

/**
 * Hook to access citation tooltip context.
 * Must be used within CitationTooltipProvider.
 */
export const useCitationTooltip = (): CitationTooltipContextType => {
  const context = useContext(CitationTooltipContext);
  if (!context) {
    throw new Error("useCitationTooltip must be used within CitationTooltipProvider");
  }
  return context;
};

export default CitationTooltipContext;
